import {
  faClipboard,
  faFilm,
  faPen,
  faTrash,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import HtmlText from "../../CommonComponents/HtmlText";
import Paginate from "../../CommonComponents/Pagination/Paginate";
import { networkService } from "../../config/axios";
import Button from "../../Elements/Button";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";
import CustomTooltip from "../../Elements/Tooltip";
import CommonModal from "../../Modals/CommonModal";
import DataPerPage from "../../CommonComponents/Pagination/DataPerPage";
import StatsFilters from "./StatsFilters";
import StatsTableView from "./StatsTableView";
import UpdateRecipientModal from "./UpdateRecipientModal";
import { useStats } from "./StatsContext";

const DEFAULT_PER_PAGE = 10;

const Views = () => {
  const {
    tl,
    doc,
    pdfFile: { thumbs_details },
  } = window?.objInit;
  const { selectedRecipientId, selectedDocsFileId } = useStats();
  const [filterPayload, setFilterPayload] = useState({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    type: tl ? "tl" : "doc",
  });
  const [viewDataList, setViewDataList] = useState(null);
  const [isLoading, setIsLoading] = useState(true)
  const [editRecipientModal, setEditRecipientModal] = useState(null);
  const [potentialBoatItem, setPotentialBoatItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  // Only true after a successful filter apply; false on initial load, Reset, or API error (so "visits match" message shows only for successful filtered results)
  const [showFilterMatchMessage, setShowFilterMatchMessage] = useState(false);
  const { pathname } = useLocation();
  const initialLoadRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Global pending request tracker for component-level deduplication
  if (typeof window !== 'undefined' && !window.__STATS_VIEWS_PENDING_REQUEST__) {
    window.__STATS_VIEWS_PENDING_REQUEST__ = null;
  }
  const handleApplyFilter = async (filterData, type = null) => {
    // Check if any filters are actually applied (excluding direction, page, per_page, type, token)
    const hasFilters = filterData && Object.keys(filterData).some(key => {
      if (['direction', 'page', 'per_page', 'type', 'token'].includes(key)) {
        return false;
      }
      const value = filterData[key];
      return value !== null && value !== undefined && value !== '';
    });

    // On Reset: clear applied filters and hide the message immediately
    if (type === 'reset') {
      setAppliedFilters({});
      setShowFilterMatchMessage(false);
    }

    // Reset to page 1 when applying/resetting filters
    setFilterPayload((prev) => ({ ...prev, page: 1 }));

    const perPage = filterPayload.per_page ?? DEFAULT_PER_PAGE;
    let payload = {
      ...filterData,
      ...filterPayload,
      page: 1,
      per_page: perPage,
      direction: type == null ? "start_time_desc" : "first_last_asc",
      name: filterData?.recipient,
      token: tl?.token || doc?.group_id,
    };
    if (selectedRecipientId) {
      payload.receiver_id = selectedRecipientId;
    }
    if (selectedDocsFileId) {
      payload.docs_file_id = selectedDocsFileId;
    }

    setIsLoading(true);
    try {
      const { data, status } = await networkService(
        `/statistics/sessions?${new URLSearchParams(payload).toString()}`,
        "GET"
      );
      setViewDataList((prev) => ({
        ...prev,
        ...data,
        page: 1,
        per_page: perPage,
        sessions: data.sessions,
        sessions_no: data?.sessions_no,
      }));
      // Only store applied filters and show message when the API call succeeded (not on Reset)
      if (type !== 'reset') {
        setAppliedFilters(hasFilters ? filterData : {});
        setShowFilterMatchMessage(!!hasFilters);
      }
    } catch (error) {
      setShowFilterMatchMessage(false);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDataList = async (payloadGet = {}) => {
    // Prevent duplicate concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    const filterParams = { ...appliedFilters };
    ["direction", "page", "per_page", "type", "token"].forEach((k) => delete filterParams[k]);
    if (filterParams.recipient != null) {
      filterParams.name = filterParams.recipient;
    }

    const perPage = payloadGet.per_page ?? filterPayload.per_page ?? DEFAULT_PER_PAGE;
    let payload = {
      ...filterPayload,
      ...filterParams,
      per_page: perPage,
      direction: "start_time_desc",
      token: tl?.token || doc?.group_id,
      ...payloadGet,
    };
    if (selectedRecipientId) {
      payload.receiver_id = selectedRecipientId;
    }
    if (selectedDocsFileId) {
      payload.docs_file_id = selectedDocsFileId;
    }

    // Create a stable payload key for deduplication
    const payloadKey = JSON.stringify(payload);
    const url = `/statistics/sessions?${new URLSearchParams(payload).toString()}`;

    // Clear pending request if payload differs (e.g., pagination/filter change)
    if (typeof window !== 'undefined' && window.__STATS_VIEWS_PENDING_REQUEST__) {
      const existing = window.__STATS_VIEWS_PENDING_REQUEST__;
      if (existing.payloadKey !== payloadKey) {
        window.__STATS_VIEWS_PENDING_REQUEST__ = null;
      }
    }

    // Check if there's already a pending request with the same payload (component-level deduplication)
    // This works alongside networkService's deduplication to prevent duplicate calls from StrictMode
    const pendingRequest = window.__STATS_VIEWS_PENDING_REQUEST__;
    if (pendingRequest && pendingRequest.payloadKey === payloadKey && pendingRequest.promise) {
      // Update loading state and wait for the existing promise
      setIsLoading(true);

      try {
        const { data, status } = await pendingRequest.promise;
        setIsLoading(false);
        setViewDataList((prev) => ({
          ...prev,
          ...data,
          ...filterPayload,
          per_page: payloadGet.per_page ?? filterPayload.per_page ?? DEFAULT_PER_PAGE,
          sessions: data.sessions,
          sessions_no: data?.sessions_no ?? (data.sessions?.length > 0 ? data.sessions[0].cnt : 0),
        }));
      } catch (error) {
        setIsLoading(false);
        console.error(error);
      }

      return; // Exit early, we're using the shared promise
    }

    // No pending request, create a new one
    setIsLoading(true);
    isFetchingRef.current = true;

    // Create the request promise and store it globally for deduplication
    const requestPromise = networkService(url, "GET");

    // Store the pending request globally so other calls can reuse it
    if (typeof window !== 'undefined') {
      window.__STATS_VIEWS_PENDING_REQUEST__ = {
        payloadKey: payloadKey,
        promise: requestPromise,
        timestamp: Date.now()
      };
    }

    try {
      const { data, status } = await requestPromise;
      setIsLoading(false);
      setViewDataList((prev) => ({
        ...prev,
        ...data,
        ...filterPayload,
        per_page: payloadGet.per_page ?? filterPayload.per_page ?? DEFAULT_PER_PAGE,
        sessions: data.sessions,
        sessions_no: data?.sessions_no ?? (data.sessions?.length > 0 ? data.sessions[0].cnt : 0),
      }));
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    } finally {
      isFetchingRef.current = false;

      // Clear the pending request after a short delay to allow StrictMode remounts to complete
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.__STATS_VIEWS_PENDING_REQUEST__) {
          const pending = window.__STATS_VIEWS_PENDING_REQUEST__;
          const timeSinceRequest = Date.now() - pending.timestamp;
          // Only clear if it's been more than 100ms (enough time for StrictMode remounts)
          if (timeSinceRequest > 100) {
            window.__STATS_VIEWS_PENDING_REQUEST__ = null;
          }
        }
      }, 150);
    }
  };

  const handleUpdateReceipient = async (formData) => {
    let payload = {};
    payload["receiver"] = {
      ...formData,
      name: `${formData?.first_name} ${formData?.last_name}`,
    };
    if (formData?.id == undefined) {
      payload["company_id"] = editRecipientModal?.tracking_link?.company_id;
      payload["session_id"] = editRecipientModal?.id;
      payload["campaign_id"] = editRecipientModal?.tracking_link?.campaign_id;
      payload["tracking_link_id"] = editRecipientModal?.tracking_link?.id;
    }
    const { data, status } = await networkService(
      `/receivers/${formData?.id}/${formData?.id == undefined ? "add_receiver_info" : "update_receiver_info"
      }`,
      "POST",
      payload
    );
    updateDataList();
    setEditRecipientModal(null);
  };
    const copyData = (text) => {
        if (!text) return;

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text);
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
    };

  const handleUpdatePotentialBoatEvent = async () => {
    try {
      const { data, status } = networkService(
        `mark_as_bot/${potentialBoatItem}`,
        "PATCH"
      );
      updateDataList();
      setPotentialBoatItem(null);
    } catch (error) {
      console.error(error);
    }
  };
  const handleItemToDeleteEvent = async () => {
    try {
      const { data, status } = networkService(
        `soft_delete_session/${itemToDelete}`,
        "PATCH"
      );
      updateDataList();
      setItemToDelete(null);
    } catch (error) {
      console.error(error);
    }
  };
  const handleLoadNewData = (page) => {
    setFilterPayload({
      ...filterPayload,
      page: page
    });
    updateDataList({ page: page });
  };

  const handlePerPageChange = (value) => {
    const perPage = Number(value) || DEFAULT_PER_PAGE;
    setFilterPayload((prev) => ({ ...prev, page: 1, per_page: perPage }));
    updateDataList({ page: 1, per_page: perPage });
  };

  useEffect(() => {
    // Prevent duplicate initial load calls within the same mount
    // Allow StrictMode remounts to proceed - component-level deduplication will handle them
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;
    updateDataList();
  }, []);

  // Re-fetch when recipient filter changes
  const prevRecipientRef = useRef(selectedRecipientId);
  useEffect(() => {
    if (prevRecipientRef.current !== selectedRecipientId) {
      prevRecipientRef.current = selectedRecipientId;
      // Reset fetch guards so this request goes through even if one is in-flight
      isFetchingRef.current = false;
      if (typeof window !== 'undefined') {
        window.__STATS_VIEWS_PENDING_REQUEST__ = null;
      }
      setFilterPayload((prev) => ({ ...prev, page: 1 }));
      updateDataList({ page: 1 });
    }
  }, [selectedRecipientId]);

  // Re-fetch when language filter changes
  const prevDocsFileRef = useRef(selectedDocsFileId);
  useEffect(() => {
    if (prevDocsFileRef.current !== selectedDocsFileId) {
      prevDocsFileRef.current = selectedDocsFileId;
      isFetchingRef.current = false;
      if (typeof window !== 'undefined') {
        window.__STATS_VIEWS_PENDING_REQUEST__ = null;
      }
      setFilterPayload((prev) => ({ ...prev, page: 1 }));
      updateDataList({ page: 1 });
    }
  }, [selectedDocsFileId]);

  const columns = React.useMemo(() => {
    return [
      {
        label: "Visits",
        key: "date",
        render: (value, row) => (
          <CustomTooltip title={<p className="p-1 text-light">Click to reveal this recipient's journey which started on {value}</p>}>
            <a
              href={`${pathname}${row?.receiver?.id == undefined ? "#/session/" : "#/viewer/"
                }${row?.receiver?.id != undefined ? `${row?.receiver?.id}&` : ""
                }${row.id}${row?.receiver?.id == undefined ? ",session" : ""}`}
              className={``}
            >
              {value}
            </a>
          </CustomTooltip>
        ),
      },
      {
        label: "Link",
        key: "link",
        render: (value, row) => {
          return <div>
            <CustomTooltip title={<p className="p-1 text-light">{row?.tracking_link_tooltip}</p>}>
              <a href={`/tl-stats/${row?.tracking_link?.token}#/views`}>{row?.tracking_link?.id}</a>
            </CustomTooltip>
            <a
              href={`/links/tl/${row?.tracking_link?.token}`}
              className={``}
            >
              <p>LP={row?.landing_page?.page}</p>
            </a>
          </div>
        },
      },
      {
        label: "Recipient",
        key: "recipient",
          render: (value, row) => {
              if (row?.other_bot) {
                  return (
                      <div>
                          <div
                              style={{
                                  display: "flex",
                                  gap: "12px",
                                  alignItems: "center",
                              }}
                          >
                              <CustomTooltip
                                  title={
                                      <p className="p-1 text-light">
                                          Nr. of users who reported this IP address as a bot visit
                                      </p>
                                  }
                              >
                                  <span style={{cursor: "pointer"}}>
                                    {row?.bot_count ?? "-"}
                                  </span>
                              </CustomTooltip>

                              <CustomTooltip
                                  title={
                                      <p className="p-1 text-light">
                                          Click to investigate further using a third-party service.
                                      </p>
                                  }
                              >
                                  <span>
                                    <a
                                        href={`https://www.iplocation.net/search?q=${row?.ip}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                      {row?.ip_isp ?? "ISP"}
                                    </a>
                                  </span>
                              </CustomTooltip>
                          </div>

                          <p style={{marginTop: "4px"}}>
                              {row?.ip ?? "-"}
                          </p>
                      </div>
                  );
              }

              return (
                    <div className={`${!value ? "hide-icon" : "image-wrapper"}`}>
                        {value ? (
                            <CustomTooltip
                                title={<p className="p-1 text-light">{row?.receiver?.email || "-"}</p>}
                            >
                                <span>{value}</span>
                            </CustomTooltip>
                        ) : (
                            "Unknown"
                        )}

                        <CustomTooltip title={<p className="p-1 text-light">Copy Email</p>}>
                            <FontAwesomeIcon
                                icon={faClipboard}
                                className="visit-icon visit-edit-icon bg-transparent"
                                onClick={() =>
                                    copyData(row?.receiver?.email)
                                }
                            />
                        </CustomTooltip>

                        <CustomTooltip title={<p className="p-1 text-light">Edit</p>}>
                            <FontAwesomeIcon
                                icon={faPen}
                                className={`${!value ? "visit-edit-icon" : "visit-icon bg-transparent"} success-green`}
                                onClick={() => setEditRecipientModal(row)}
                            />
                        </CustomTooltip>
                    </div>
                );
            },
        },
        {
        label: "Location",
        key: "location",
        render: (value, row) => (
            <div className="location-cell d-flex align-items-center gap-2">
                <CustomTooltip title={<p className="p-1 text-light">{row?.country ?? "-"}</p>}>
                    <div
                        style={{ width: "22px" }}
                        className={`flag-icon flag-icon-${row?.country_code?.toLowerCase()}`}
                    />
                </CustomTooltip>

                <CustomTooltip title={<p className="p-1 text-light">{row?.ip}</p>}>
                    <a
                        href={`https://google.com/maps/place/${row?.ip_details?.location?.lat},${row?.ip_details?.location?.long}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-nowrap"
                    >
                        {value}
                    </a>
                </CustomTooltip>

                <CustomTooltip title={<p className="p-1 text-light">Copy IP</p>}>
                    <FontAwesomeIcon
                        icon={faClipboard}
                        className="copy-ip-icon bg-transparent"
                        onClick={() => copyData(row?.ip)}
                    />
                </CustomTooltip>
            </div>
        ),
      },
      {
        label: "Device Info",
        key: "browser",
        render: (value, row) => (
          <div>
            <CustomTooltip title={<p className="p-1 text-light">{row?.browser_info?.browser}</p>}>
              <span className="pe-2">
                <img src={`/browser/${row?.browser_info?.browser_icon?.toLowerCase()}`} />
              </span>
            </CustomTooltip>
            <CustomTooltip title={<p className="p-1 text-light">{row?.browser_info?.os_family}</p>}>
              <span className="pe-2">
                <img src={`/os/${row?.browser_info?.os_icon}`} />
              </span>
            </CustomTooltip>
              <CustomTooltip
                  title={
                      <p className="p-1 text-light">
                        {row?.browser_info?.device ?? "-"}
                      </p>
                  }
              >
                  <img src={`/device/${row?.browser_info?.device_icon}`} alt="" />
              </CustomTooltip>
          </div>
        ),
      },
      {
        label: "Score",
        renderHeader: (value) => (
          <CustomTooltip
            title={<p className="p-1 text-light">(Number of sessions with selected engagement pages over the threshold * Number of engagement pages over the threshold) / (Total number of sessions * Total number of pages)</p>}
          >
            <p className="cursor-pointer">{value?.label}</p>
          </CustomTooltip>
        ),
        key: "engagement",
        render: (value, row) => <p>{value?.replace("%", "")}</p>,
      },
      {
        label: "Viewed",
        renderHeader: (value, row) => (
          <CustomTooltip
            title={<p className="p-1 text-light">Percentage this doc was viewed during each visit</p>}
          >
            <p className="cursor-pointer">{value?.label} </p>
          </CustomTooltip>
        ),
        key: "high_priority_elements",
        render: (value, row) => (
          <div className="d-flex align-items-center gap-2 cursor-pointer">
            <CustomTooltip title={<p className="p-1 text-light">{row?.number_of_viewed_pages}</p>}>
              <p>{row?.percent_viewed}</p>
            </CustomTooltip>

            {Array.isArray(value) && value.slice(0, 3).map((item, index) => {
              /* Support both API formats:
                 Flat:    { type, message, icon }
                 Grouped: { type, data: [{ message, icon }, ...] } */
              let icon, tooltipHtml;

              if (item?.message) {
                icon = item.icon;
                tooltipHtml = item.message;
              } else if (Array.isArray(item?.data) && item.data.length > 0) {
                icon = item.data[0]?.icon;
                tooltipHtml = item.data
                  .filter(d => d?.message)
                  .map(d => d.message)
                  .join('<br/>');
              }

              if (!tooltipHtml) return null;

              return (
                <CustomTooltip
                  key={`hp-icon-${index}`}
                  title={<p className="p-1 text-light" dangerouslySetInnerHTML={{ __html: tooltipHtml }}></p>}
                >
                  <span className={`video-icn-top ${icon || ''}`}></span>
                </CustomTooltip>
              );
            })}
          </div>
        ),
      },
      {
        label: "Time",
        renderHeader: (value) => (
          <CustomTooltip
            title={<p className="p-1 text-light">Total time spent viewing this KDF was {tl?.stats?.min_viewed || doc?.stats?.min_viewed} (HH:MM:SS)</p>}
          >
            <p className="cursor-pointer">{value?.label}</p>
          </CustomTooltip>
        ),
        key: "min_viewed",
        render: (value, row) => (
          <div className="d-flex align-items-center gap-2 cursor-pointer">
          <CustomTooltip
            title={<HtmlText htmlContent={row?.session_pages_spend_time} />}
          >
            <p>{value}</p>
          </CustomTooltip>
          </div>
        ),
      },
        {
            label: "Top Page",
            renderHeader: (value) => <p>{value?.label}</p>,
            key: "most_viewed_pages",
            render: (value, row) => {
                const timePerPage = row?.time_per_page || [];
                const topPageData = timePerPage.reduce((maxObj, currentObj) => {
                    return currentObj.secs_on_page > (maxObj.secs_on_page || 0) ? currentObj : maxObj;
                }, {});
                const topPage = topPageData.page;
                const topPageSecs = topPageData.secs_on_page || 0;
                const totalSecs = timePerPage.reduce((sum, p) => sum + (p.secs_on_page || 0), 0);
                const percentage = totalSecs > 0 ? ((topPageSecs / totalSecs) * 100).toFixed(0) : 0;

                return (
                    <CustomTooltip
                        light={true}
                        title={
                            <div style={{ width: "140px", textAlign: "center", padding: "8px" }}>
                                <img
                                    src={`${thumbs_details?.normal}${(topPage || 1) - 1}.${thumbs_details?.extension}`}
                                    alt={`Page ${topPage}`}
                                    style={{ width: "100%", borderRadius: "4px", border: "1px solid #e0e0e0" }}
                                />
                                <p style={{ color: "#555", fontSize: "11px", marginTop: "6px", marginBottom: 0, lineHeight: "1.4" }}>
                                    User spent {percentage}% ({topPageSecs} seconds) of total time on this page
                                </p>
                            </div>
                        }
                    >
                        <p className="cursor-pointer">{topPage}</p>
                    </CustomTooltip>
                );
            },
        },
        {
        label: "Exit Page",
        renderHeader: (value) => <p>{value?.label}</p>,
        key: "exit_page",
        render: (value, row) => (
          <CustomTooltip
            title={
              <div style={{ width: "140px", textAlign: "center", padding: "8px" }}>
                <img
                  src={`${thumbs_details?.normal}${row?.popular_exit_pages?.[0]?.page - 1
                    }.${thumbs_details?.extension}`}
                  alt={row?.popular_exit_pages?.[0]?.pages}
                  style={{ width: "100%", borderRadius: "4px", border: "1px solid #e0e0e0" }}
                />
              </div>
            }
          >
            <p className="cursor-pointer">{row?.pages?.filter((page) => page?.exit_page)[0]?.page}</p>
          </CustomTooltip>
        ),
      },
    ];
  }, [viewDataList]);
  const renderActions = (row) => (
    <div className="stats-action-icons">
      <CustomTooltip
        title={
          <p className="p-1 text-light">
            {row?.other_bot
              ? row?.bot_count === 1
                ? "You are the 1st person to alert us to this potential bot. Thank you. We will confirm/reject the bot visit shortly."
                : "2 persons have alerted us to this potential bot, including you. Thank you. We will confirm/reject the bot visit shortly."
              : "Click this icon to report a potential bot visit."}
          </p>
        }
      >
        <img
          src="/icons/robot.svg"
          className="stats-action-icon cursor-pointer"
          alt="Bot"
          style={{
            filter: 'invert(55%) sepia(85%) saturate(2000%) hue-rotate(5deg) brightness(100%) contrast(105%)'
          }}
          onClick={() => {
            if (!row?.other_bot) {
              setPotentialBoatItem(row?.id);
            }
          }}
        />
      </CustomTooltip>

      <CustomTooltip title={<p className="p-1 text-light">Delete Visit</p>}>
        <img
          src="/icons/danger-delete.svg"
          className="stats-action-icon cursor-pointer"
          alt="Delete"
          onClick={() => setItemToDelete(row?.id)}
        />
      </CustomTooltip>
    </div>
  );

  return (
    <>
      <UpdateRecipientModal
        editRecipientModal={editRecipientModal}
        setEditRecipientModal={setEditRecipientModal}
        submitRecipientForm={handleUpdateReceipient}
      />
      <CommonModal
        title={"Mark as potential bot"}
        modalStatus={potentialBoatItem != null}
        changeActiveStatus={() => setPotentialBoatItem(null)}
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"No"}
              classList={"alert"}
              addonClass={"my-2 mx-2"}
              onClick={() => setPotentialBoatItem(null)}
            />
            <Button
              text={"Yes"}
              classList={"success"}
              type="submit"
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={() => handleUpdatePotentialBoatEvent()}
            />
          </div>
        }
      >
        <p className="text-center" style={{ fontSize: "14px",  }}>
          Are you sure you want to mark this visit as bot?
        </p>
      </CommonModal>
      <CommonModal
        title={"Delete Visit"}
        modalStatus={itemToDelete != null}
        changeActiveStatus={() => setItemToDelete(null)}
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"No"}
              classList={"alert"}
              type="button"
              addonClass={"my-2 mx-2"}
              onClick={() => setItemToDelete(null)}
            />
            <Button
              text={"Yes"}
              classList={"success"}
              type="submit"
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={() => handleItemToDeleteEvent()}
            />
          </div>
        }
      >
        <p className="text-center" style={{ fontSize: "14px" }}>
          Are you sure you want to delete the stats for this visit?
        </p>
      </CommonModal>

      <StatsFilters
        threshold={(tl || doc)?.page_viewed_sec ?? "-"}
        onFilterChange={handleApplyFilter}
        defaultDirection="start_time_desc"
        token={tl?.token || doc?.group_id}
      />
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <>
          {(viewDataList?.sessions_no ?? 0) > (filterPayload?.per_page ?? DEFAULT_PER_PAGE) && (
            <div className="show-select1 d-flex align-items-baseline mt-3">
              <DataPerPage
                dataPerPage={filterPayload?.per_page ?? DEFAULT_PER_PAGE}
                setDataPerPage={handlePerPageChange}
              />
            </div>
          )}
          {viewDataList?.sessions?.length > 0 ? (
            <>
              <StatsTableView
                columns={columns}
                data={viewDataList?.sessions?.map((it) => {
                  return {
                    ...it,
                    date: it?.date,
                    recipient: it?.receiver?.name,
                    location: [it?.ip_details?.city, it?.ip_details?.subdivision_code].filter(val => val != null).join(' '),
                    country_code: it?.ip_details?.country_code,
                    country: it?.ip_details?.country,
                  };
                })}
                renderActions={renderActions}
                hasAppliedFilters={showFilterMatchMessage && Object.keys(appliedFilters).length > 0}
                totalMatched={viewDataList?.sessions_no}
              />
              {(viewDataList?.sessions_no ?? 0) > (filterPayload?.per_page ?? DEFAULT_PER_PAGE) && (
                <Paginate
                  currentPage={filterPayload?.page}
                  totalPages={Math.ceil(
                    (viewDataList?.sessions_no ?? 0) / (filterPayload?.per_page ?? DEFAULT_PER_PAGE)
                  )}
                  onPageChange={handleLoadNewData}
                  totalData={viewDataList?.sessions_no}
                  itemTitle={""}
                  hideText={true}
                  pageSize={filterPayload?.per_page ?? DEFAULT_PER_PAGE}
                  currentCount={viewDataList?.sessions?.length}
                />
              )}
            </>
          ) : (
            <div className="back-alert p-3 text-white mt-5">
              <FontAwesomeIcon icon={faUserXmark} /> Sorry, no unique recipients
              were found. Either none of your recipients opted-in or the ET needs to
              be reduced to reveal identified recipients.
            </div>
          )}
        </>
      )}
    </>
  );
};
export default Views;
